import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";

const landlordLabel = (landlord) => {
  if (!landlord) return "";
  const count = landlord.property_count ?? landlord.properties?.length ?? 0;
  if (count === 0) return `${landlord.name} (no properties yet)`;
  if (count === 1) return `${landlord.name} (1 property)`;
  return `${landlord.name} (${count} properties)`;
};

export default function AssignAnonymousTenancy({
  open,
  row,
  landlords = [],
  loadingLandlords = false,
  onClose,
  onAssign,
  isSaving,
  errorMessage,
}) {
  const [selectedLandlord, setSelectedLandlord] = useState(null);
  const [rentalId, setRentalId] = useState("");

  const sortedLandlords = useMemo(
    () =>
      [...landlords].sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, { sensitivity: "base" })
      ),
    [landlords]
  );

  useEffect(() => {
    if (open) {
      setSelectedLandlord(null);
      setRentalId("");
    }
  }, [open, row]);

  const properties = selectedLandlord?.properties || [];
  const needsPropertyPick = properties.length > 1;
  const hasNoProperties = Boolean(selectedLandlord) && properties.length === 0;

  useEffect(() => {
    if (!selectedLandlord) {
      setRentalId("");
      return;
    }
    if (selectedLandlord.properties?.length === 1) {
      setRentalId(String(selectedLandlord.properties[0].id));
      return;
    }
    setRentalId("");
  }, [selectedLandlord]);

  const tenantName = `${row?.tenant?.first_name || ""} ${row?.tenant?.last_name || ""}`.trim();
  const canSubmit =
    Boolean(selectedLandlord) &&
    (!needsPropertyPick || Boolean(rentalId)) &&
    !isSaving &&
    !loadingLandlords;

  const handleSubmit = (event) => {
    event.preventDefault();
    if (!canSubmit || !selectedLandlord) return;
    onAssign?.({
      landlord_id: Number(selectedLandlord.id),
      rental_id: rentalId ? Number(rentalId) : null,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>Assign to Existing Landlord</DialogTitle>
        <DialogContent>
          <Box display="grid" gap={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Move <strong>{tenantName || "this tenant"}</strong> ({row?.unit?.name || "unit"}) from{" "}
              <strong>{row?.landlord?.name || "anonymous landlord"}</strong> onto a real landlord
              property. Unit details and the active period stay with the tenant. The anonymous
              landlord is removed when no other units remain.
            </Typography>

            <Autocomplete
              options={sortedLandlords}
              loading={loadingLandlords}
              disabled={loadingLandlords || isSaving}
              value={selectedLandlord}
              onChange={(_, value) => setSelectedLandlord(value)}
              getOptionLabel={landlordLabel}
              isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
              filterOptions={(options, state) => {
                const query = (state.inputValue || "").trim().toLowerCase();
                if (!query) return options;
                return options.filter((option) =>
                  (option?.name || "").toLowerCase().includes(query)
                );
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Landlord"
                  required
                  placeholder="Search landlords…"
                />
              )}
              noOptionsText={
                loadingLandlords ? "Loading landlords…" : "No matching landlords"
              }
            />

            {selectedLandlord && needsPropertyPick ? (
              <FormControl fullWidth required disabled={isSaving}>
                <InputLabel>Property</InputLabel>
                <Select
                  label="Property"
                  value={rentalId}
                  onChange={(event) => setRentalId(event.target.value)}
                >
                  {properties.map((property) => (
                    <MenuItem key={property.id} value={String(property.id)}>
                      {property.name}
                      {property.location ? ` — ${property.location}` : ""}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : null}

            {selectedLandlord && !needsPropertyPick && properties[0] ? (
              <Alert severity="info">
                This landlord has one property — assignment will go to{" "}
                <strong>{properties[0].name}</strong>.
              </Alert>
            ) : null}

            {hasNoProperties ? (
              <Alert severity="info">
                This landlord has no properties yet. A new property will be created for them from{" "}
                <strong>{row?.property?.name || "the current property"}</strong>, and this unit will
                move there as their first property.
              </Alert>
            ) : null}

            {errorMessage ? (
              <Alert severity="error">{errorMessage}</Alert>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <LoadingButton type="submit" variant="contained" loading={isSaving} disabled={!canSubmit}>
            Assign
          </LoadingButton>
        </DialogActions>
      </form>
    </Dialog>
  );
}
